import type { Outcome, Principal, Problem } from '@erp-sslab/sdk-frontend';
import { scrubbed } from './offline-reads.ts';

/**
 * The offline outbox (docs/design/offline-outbox.md §2–§3, phases 2–3; Fable's B2): a form whose
 * action declares `offline: true`, sent when its unit cannot be reached, is kept on this device and
 * sent again later — with the same Idempotency-Key, so the server answers the first result again
 * instead of writing twice.
 *
 * What is kept, and what never is:
 * - Only what the form would have sent — the method, the path, the body — and the words the outbox
 *   panel shows it by. Never a token, a header or a password: the request is sent with the session's
 *   token of the moment, and an item waits while nobody is signed in. A body with a property named
 *   like a secret is not kept at all (`keepable`) — `loadUnit` already refuses `secret` fields on an
 *   `offline` action, and this is the shell's own second line.
 * - Keyed by workspace and person (`outbox:<tenant>:<user>:`), so a shared phone never shows or sends
 *   one person's queue as another's. One IndexedDB database, `core-outbox`, one store, mirroring the
 *   kept reads (offline-reads.ts); signing out deletes the whole database (`forgetDevice`).
 * - Nothing in the service worker, nothing in `localStorage`.
 *
 * When it sends: on start, on resume, on the browser's `online` event and every minute while items
 * wait (`startOutboxReplay`) — one item at a time per unit, in the order they were made, because a
 * later report may depend on an earlier one. Only a request that got no answer at all waits to be
 * sent again (with a growing pause: 5 s, 30 s, 2 min, then every 5 min). An answer the server gave is
 * final: 2xx removes the item; a refusal (400, 403, 404, 409, 422 …) marks it «needs attention», and
 * it is never resent automatically — the person reads why and discards it. A 401 waits for sign-in.
 */

/** The IndexedDB database the outbox lives in; deleted whole on sign-out. */
export const OUTBOX_DB = 'core-outbox';
const STORE = 'outbox';
/** The pauses after each attempt that got no answer; the last repeats. */
export const BACKOFF_MS: readonly number[] = [5_000, 30_000, 120_000, 300_000];
/** How often the shell tries again while something waits. */
export const REPLAY_EVERY_MS = 60_000;
/** The longest Idempotency-Key the SDK accepts (sdk-backend-ts `IDEMPOTENCY_KEY_MAX`). */
const KEY_MAX = 120;

export type OutboxState = 'waiting' | 'sending' | 'attention';

/** One request kept to be sent: what the form would have sent, and nothing more. */
export interface OutboxItem {
  /** A ULID: unique, and in the order the items were made. */
  id: string;
  /** The Idempotency-Key, made when Send was pressed and carried by every attempt. */
  key: string;
  /** The module the action belongs to, and where its unit answers. */
  module: string;
  base: string;
  /** Which action of which screen, for the panel's words and its «open». */
  action: string;
  screen: string;
  label: string;
  label_key?: string;
  /** What the row or record is called, when the action is about one. */
  about?: string;
  method: string;
  path: string;
  body?: unknown;
  /** When Send was pressed, in milliseconds. */
  created_at: number;
  attempts: number;
  state: OutboxState;
  /** Why it needs attention, as the server said it — or why the last attempt got no answer. */
  last_error?: string;
  problems?: Problem[];
  status?: number;
}

/** What a form hands the outbox: the item before the outbox gives it its id, times and state. */
export type OutboxDraft = Omit<OutboxItem, 'id' | 'created_at' | 'attempts' | 'state' | 'last_error' | 'problems' | 'status'> & { created_at?: number };

/** Where the items live — IndexedDB in a browser, a map in a test. Every call is about one owner's items. */
export interface OutboxBackend {
  list(owner: string): Promise<OutboxItem[]>;
  put(owner: string, item: OutboxItem): Promise<void>;
  remove(owner: string, id: string): Promise<void>;
  /** Everything, every owner's: signing out. */
  clear(): Promise<void>;
}

/** Whose items these are — `outbox:<tenant>:<user>:` — or null when nobody is in a workspace. */
export function outboxOwner(principal: Principal | null): string | null {
  if (!principal?.sub || !principal.tenant_id) return null;
  return `outbox:${principal.tenant_id}:${principal.sub}:`;
}

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** A ULID: ten characters of time, sixteen of chance — sorted as text, in the order they were made. */
export function ulid(now: number = Date.now(), random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  let time = '';
  let t = Math.max(0, Math.floor(now));
  for (let i = 0; i < 10; i++) { time = CROCKFORD[t % 32] + time; t = Math.floor(t / 32); }
  const bytes = random(16);
  let rest = '';
  for (let i = 0; i < 16; i++) rest += CROCKFORD[bytes[i]! % 32];
  return time + rest;
}

/**
 * The Idempotency-Key an item is born with, when Send is pressed and before the first attempt:
 * `ob:<user>:<ulid>`, in the SDK's alphabet (letters, digits, `. _ : -`) and never longer than it
 * accepts. The person's id is cut short rather than the ULID, which is what makes the key unique.
 */
export function outboxKey(principal: Principal | null, id: string = ulid()): string {
  const user = (principal?.sub ?? 'anon').replace(/[^A-Za-z0-9._-]/g, '-');
  const room = KEY_MAX - 'ob::'.length - id.length;
  return `ob:${user.slice(0, Math.max(1, room))}:${id}`;
}

/**
 * The body an `offline` action sends, first attempt and kept item alike: with `client_created_at` —
 * the moment Send was pressed, as an ISO date and time — when the action says its route takes it
 * (`client_created_at: true`, which `loadUnit` allows only where the route's request body declares the
 * property, offline-outbox.md §7.4), and unchanged otherwise: a route refuses a property it does not know.
 */
export function stampedBody<T>(action: { offline?: boolean; client_created_at?: boolean }, body: T, created: number): T {
  if (action.offline !== true || action.client_created_at !== true || !body || typeof body !== 'object' || Array.isArray(body)) return body;
  return { ...(body as Record<string, unknown>), client_created_at: new Date(created).toISOString() } as T;
}

/** Whether a body may be kept on the device: never one holding a property named like a secret. */
export function keepable(body: unknown): boolean {
  return JSON.stringify(scrubbed(body ?? null)) === JSON.stringify(body ?? null);
}

/** Whether an attempt got no answer at all — no connection, a timeout, an edge or unit that was down. */
export function gotNoAnswer(r: Outcome<unknown>): boolean {
  return r.state === 'timeout' || (r.state === 'unavailable' && r.status === undefined);
}

/** What one attempt came to. */
export type Verdict = 'sent' | 'retry' | 'signin' | 'attention';

/**
 * What an answer means for the item: sent; try again later (no answer); wait for sign-in (the token
 * was refused and nobody is signed in now); or a refusal the person has to read («needs attention»).
 */
export function verdictOf(r: Outcome<unknown>, signedIn: boolean): Verdict {
  if (r.state === 'ok') return 'sent';
  if (gotNoAnswer(r)) return 'retry';
  if (r.state === 'forbidden' && !signedIn) return 'signin';
  return 'attention';
}

/** The items kept in memory: for tests, and the shape the IndexedDB backend answers. */
export function memoryOutbox(): OutboxBackend {
  const owners = new Map<string, Map<string, OutboxItem>>();
  const of = (owner: string) => { let m = owners.get(owner); if (!m) { m = new Map(); owners.set(owner, m); } return m; };
  return {
    list: async (owner) => [...of(owner).values()].map((i) => structuredClone(i)),
    put: async (owner, item) => { of(owner).set(item.id, structuredClone(item)); },
    remove: async (owner, id) => { of(owner).delete(id); },
    clear: async () => { owners.clear(); },
  };
}

/** IndexedDB request as a promise. */
function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });
}

/**
 * The items in IndexedDB: one database, one store, each row `{id: owner + item id, owner, item}` with
 * an index on its owner. Null where the browser has none (a test, some private windows) — then an
 * offline action is refused as it always was, rather than kept somewhere it could be lost.
 */
export function indexedDbOutbox(factory: IDBFactory | undefined = globalThis.indexedDB): OutboxBackend | null {
  if (!factory) return null;
  let opening: Promise<IDBDatabase> | null = null;
  const open = () => {
    opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = factory.open(OUTBOX_DB, 1);
      req.onupgradeneeded = () => {
        const store = req.result.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('owner', 'owner', { unique: false });
      };
      req.onsuccess = () => {
        const db = req.result;
        // Another tab deleting the database (its sign-out) closes this one rather than blocking it.
        db.onversionchange = () => { db.close(); opening = null; };
        resolve(db);
      };
      req.onerror = () => { opening = null; reject(req.error); };
      req.onblocked = () => { opening = null; reject(new Error('blocked')); };
    });
    return opening;
  };
  const store = async (mode: IDBTransactionMode) => (await open()).transaction(STORE, mode).objectStore(STORE);
  return {
    list: async (owner) => {
      const rows = await done((await store('readonly')).index('owner').getAll(owner)) as { owner: string; item: OutboxItem }[];
      return rows.filter((r) => r.owner === owner).map((r) => r.item);
    },
    put: async (owner, item) => { await done((await store('readwrite')).put({ id: `${owner}${item.id}`, owner, item })); },
    remove: async (owner, id) => { await done((await store('readwrite')).delete(`${owner}${id}`)); },
    clear: async () => {
      const was = opening;
      opening = null;
      if (was) { try { (await was).close(); } catch { /* it never opened */ } }
      await new Promise<void>((resolve) => {
        const req = factory.deleteDatabase(OUTBOX_DB);
        // Blocked by another tab: it closes on `versionchange`, and the deletion completes then.
        req.onsuccess = () => resolve(); req.onerror = () => resolve(); req.onblocked = () => resolve();
      });
    },
  };
}

/** How an item is sent: the session's own call, with the item's key as its Idempotency-Key. */
export type OutboxSend = (item: OutboxItem) => Promise<Outcome<unknown>>;

/** What a pass of sending did, for the toast and for the screens to read again. */
export interface FlushReport { sent: OutboxItem[]; attention: OutboxItem[]; waiting: number }

/** Items in the order they were made. */
function inOrder(items: OutboxItem[]): OutboxItem[] {
  return [...items].sort((a, b) => a.created_at - b.created_at || a.id.localeCompare(b.id));
}

/** The outbox of one browser, kept and sent under the rules above. */
export class Outbox {
  private readonly backend: OutboxBackend | null;
  private readonly now: () => number;
  private readonly listeners = new Set<() => void>();
  /** Each owner's items as last read, for the pill and the panel to draw without waiting. */
  private readonly shown = new Map<string, OutboxItem[]>();
  /** Per owner and unit: how many attempts in a row got no answer, and until when to wait. */
  private readonly pause = new Map<string, { step: number; until: number }>();
  private flushing: Promise<FlushReport> | null = null;

  constructor(backend: OutboxBackend | null, now: () => number = Date.now) {
    this.backend = backend;
    this.now = now;
  }

  /** Whether this browser can keep anything at all. */
  get available(): boolean { return this.backend !== null; }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /** The owner's items as last read (empty until the first read). */
  snapshot(owner: string | null): readonly OutboxItem[] {
    return owner ? this.shown.get(owner) ?? EMPTY : EMPTY;
  }

  private async refresh(owner: string): Promise<OutboxItem[]> {
    const items = inOrder(this.backend ? await this.backend.list(owner) : []);
    this.shown.set(owner, items);
    this.listeners.forEach((l) => l());
    return items;
  }

  /** Reads the owner's items again and tells whoever draws them. */
  async load(owner: string | null): Promise<readonly OutboxItem[]> {
    if (!owner || !this.backend) return EMPTY;
    try { return await this.refresh(owner); } catch { return this.snapshot(owner); }
  }

  /**
   * Keeps a request that got no answer. Its key is the one its first attempt carried — made when Send
   * was pressed — so a resend is the same request to the server. Answers the item, or null when it
   * cannot be kept (no IndexedDB, a body holding a secret, a full or blocked store): the form then
   * says the unit could not be reached, as it would have without the outbox.
   */
  async add(owner: string, draft: OutboxDraft, attempts = 1): Promise<OutboxItem | null> {
    if (!this.backend || !keepable(draft.body)) return null;
    const created = draft.created_at ?? this.now();
    const idFromKey = /:([0-9A-HJKMNP-TV-Z]{26})$/.exec(draft.key)?.[1];
    const item: OutboxItem = { ...draft, id: idFromKey ?? ulid(created), created_at: created, attempts, state: 'waiting' };
    try {
      await this.backend.put(owner, item);
      await this.refresh(owner);
      return item;
    } catch { return null; }
  }

  /** Lets go of an item — the person's «discard». */
  async discard(owner: string, id: string): Promise<void> {
    if (!this.backend) return;
    try { await this.backend.remove(owner, id); await this.refresh(owner); } catch { /* already gone */ }
  }

  /**
   * One pass: per unit, the waiting items in the order they were made, one at a time. A unit whose
   * item got no answer stops there and pauses (`BACKOFF_MS`); another unit is not held by it. Items
   * that need attention are never sent again. `now: true` — «send now» — does not wait out a pause.
   * One pass at a time: a second call while one runs answers that one.
   */
  flush(owner: string | null, send: OutboxSend, signedIn: () => boolean, opts: { now?: boolean } = {}): Promise<FlushReport> {
    if (!owner || !this.backend) return Promise.resolve({ sent: [], attention: [], waiting: 0 });
    this.flushing ??= this.pass(owner, send, signedIn, opts.now === true).finally(() => { this.flushing = null; });
    return this.flushing;
  }

  private async pass(owner: string, send: OutboxSend, signedIn: () => boolean, force: boolean): Promise<FlushReport> {
    const report: FlushReport = { sent: [], attention: [], waiting: 0 };
    let items: OutboxItem[];
    try { items = await this.refresh(owner); } catch { return report; }
    const units = new Map<string, OutboxItem[]>();
    for (const item of items) {
      // An item left «sending» by a page closed mid-attempt is waiting again: its key makes a resend safe.
      if (item.state === 'attention') continue;
      const list = units.get(item.base) ?? [];
      list.push(item);
      units.set(item.base, list);
    }
    for (const [base, queue] of units) {
      const at = `${owner}${base}`;
      const paused = this.pause.get(at);
      if (!force && paused && paused.until > this.now()) { report.waiting += queue.length; continue; }
      for (let i = 0; i < queue.length; i++) {
        const item = queue[i]!;
        if (!signedIn()) { report.waiting += queue.length - i; break; }
        await this.save(owner, { ...item, state: 'sending' });
        let r: Outcome<unknown>;
        try { r = await send(item); } catch (e) { r = { state: 'unavailable', detail: (e as Error).message }; }
        const verdict = verdictOf(r, signedIn());
        if (verdict === 'sent') {
          this.pause.delete(at);
          await this.backend!.remove(owner, item.id).catch(() => {});
          report.sent.push(item);
          continue;
        }
        if (verdict === 'signin') {
          // Not an attempt: the item waits, as it was, for somebody to sign in.
          await this.save(owner, { ...item, state: 'waiting' });
          report.waiting += queue.length - i;
          break;
        }
        if (verdict === 'retry') {
          const step = (paused?.step ?? -1) + 1;
          this.pause.set(at, { step, until: this.now() + BACKOFF_MS[Math.min(step, BACKOFF_MS.length - 1)]! });
          await this.save(owner, { ...item, state: 'waiting', attempts: item.attempts + 1, last_error: 'detail' in r && r.detail ? r.detail : r.state });
          report.waiting += queue.length - i;
          break;
        }
        // The server answered and refused: final for this item, which waits for the person.
        const rejected = r as Exclude<Outcome<unknown>, { state: 'ok' }>;
        const attention: OutboxItem = {
          ...item, state: 'attention', attempts: item.attempts + 1,
          last_error: 'message' in rejected ? rejected.message : rejected.detail ?? rejected.state,
          ...('problems' in rejected && rejected.problems?.length ? { problems: rejected.problems } : {}),
          ...(rejected.status !== undefined ? { status: rejected.status } : rejected.state === 'forbidden' ? { status: 403 } : {}),
        };
        await this.save(owner, attention);
        report.attention.push(attention);
      }
    }
    await this.refresh(owner).catch(() => {});
    return report;
  }

  private async save(owner: string, item: OutboxItem): Promise<void> {
    await this.backend!.put(owner, item).catch(() => {});
  }

  /** Every item of every person on this browser: signing out. */
  async forget(): Promise<void> {
    this.shown.clear();
    this.pause.clear();
    this.listeners.forEach((l) => l());
    try { await this.backend?.clear(); } catch { /* nothing was kept, or the browser has already cleared it */ }
  }
}

const EMPTY: readonly OutboxItem[] = Object.freeze([]);

/** The browser's outbox. A page with no IndexedDB keeps nothing, and an offline action is refused as before. */
export const outbox = new Outbox(indexedDbOutbox());

/** What the pill says: how many wait, how many need attention, and whether the browser is offline. */
export interface OutboxCounts { waiting: number; attention: number }

export function countsOf(items: readonly OutboxItem[]): OutboxCounts {
  let waiting = 0;
  let attention = 0;
  for (const i of items) { if (i.state === 'attention') attention++; else waiting++; }
  return { waiting, attention };
}

/**
 * What the header pill says, or null when it is not drawn (nothing waits and the browser is online):
 * «1 يحتاج انتباهك» in red before anything else, then «2 بانتظار الإرسال» in amber, then «غير متصل».
 */
export function pillOf(counts: OutboxCounts, online: boolean): { tone: 'red' | 'amber' | 'neutral'; key: string; n: number } | null {
  if (counts.attention) return { tone: 'red', key: 'shell.outbox.pill.attention', n: counts.attention };
  if (counts.waiting) return { tone: 'amber', key: 'shell.outbox.pill.waiting', n: counts.waiting };
  if (!online) return { tone: 'neutral', key: 'shell.outbox.pill.offline', n: 0 };
  return null;
}

/** The window event a screen listens to, to read its rows again once an item of its unit was sent. */
export const OUTBOX_SENT_EVENT = 'core-outbox-sent';

/** The session's part the replay needs: who is signed in, and a way to send with their token. */
export interface ReplaySession {
  principal(): Principal | null;
  send<T>(baseUrl: string, path: string, opts: { method: string; body?: unknown; headers?: Record<string, string> }): Promise<Outcome<T>>;
}

/** An item sent as the form would have sent it, with its own key. */
export function sendItem(session: ReplaySession): OutboxSend {
  return (item) => session.send<unknown>(item.base, item.path, {
    method: item.method,
    headers: { 'Idempotency-Key': item.key },
    ...(item.body === undefined ? {} : { body: item.body }),
  });
}

/**
 * The outbox at work in the page: a pass on start, on resume (`visibilitychange` to visible), on the
 * `online` event and every minute — never while the browser says it is offline. Each item sent is
 * announced (`onSent`) and the screens are told to read again. Answers how to stop it.
 */
export function startOutboxReplay(box: Outbox, session: ReplaySession, onPass: (report: FlushReport) => void = () => {}, win: Window = window): () => void {
  let stopped = false;
  const run = (force = false) => {
    if (stopped) return;
    if (win.navigator?.onLine === false) return;
    const owner = outboxOwner(session.principal());
    if (!owner) return;
    void box.flush(owner, sendItem(session), () => outboxOwner(session.principal()) === owner, { now: force }).then((report) => {
      if (stopped) return;
      if (report.sent.length) win.dispatchEvent(new CustomEvent(OUTBOX_SENT_EVENT, { detail: { units: [...new Set(report.sent.map((i) => i.base))] } }));
      onPass(report);
    });
  };
  const onVisible = () => { if (win.document.visibilityState === 'visible') run(); };
  const onOnline = () => run(true);
  win.document.addEventListener('visibilitychange', onVisible);
  win.addEventListener('online', onOnline);
  const timer = win.setInterval(() => run(), REPLAY_EVERY_MS);
  void box.load(outboxOwner(session.principal())).then(() => run());
  return () => {
    stopped = true;
    win.document.removeEventListener('visibilitychange', onVisible);
    win.removeEventListener('online', onOnline);
    win.clearInterval(timer);
  };
}
